// src/utils/api.js - Enhanced API Client with robust error handling and CORS support
class APIClient {
  constructor() {
    this.baseURL = null;
    this.token = null;
    this.isInitialized = false;
    this.initPromise = null;
    this.environment = this._detectEnvironment();
    this.retryCount = 3;
    this.retryDelay = 1000;
  }

  _detectEnvironment() {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'development';
    }
    if (window.location.hostname.includes('render.com') || window.location.hostname.includes('onrender.com')) {
      return 'production';
    }
    return 'production';
  }

  async initialize() {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._detectBackendURL();
    await this.initPromise;
    this.isInitialized = true;
  }

  async _detectBackendURL() {
    console.log('Environment detected:', this.environment);

    let possibleURLs = [];

    if (this.environment === 'development') {
      possibleURLs = [
        'http://127.0.0.1:8000',
        'http://localhost:8000',
        import.meta.env.VITE_API_URL,
      ];
    } else {
      possibleURLs = [
        import.meta.env.VITE_API_URL,
        window.location.origin,
      ];
    }

    possibleURLs = possibleURLs.filter(Boolean);
    console.log('Detecting backend API...', possibleURLs);

    for (const url of possibleURLs) {
      try {
        console.log('Trying:', url);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const response = await fetch(url + '/health', {
          method: 'GET',
          signal: controller.signal,
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        
        clearTimeout(timeoutId);
        
        if (response && response.ok) {
          this.baseURL = url;
          console.log('✅ Backend found at:', url);
          localStorage.setItem('coderated_api_url', url);
          return;
        }
      } catch (error) {
        console.log('❌ Failed:', url, '-', error.name, error.message);
      }
    }

    // Default fallback
    this.baseURL = this.environment === 'development' 
      ? 'http://127.0.0.1:8000' 
      : window.location.origin;   

    console.log('⚠️ No backend detected, defaulting to:', this.baseURL);
  }

  setAuthToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('coderated_auth_token', token);
      console.log('🔐 Token stored:', token.substring(0, 20) + '...');
    } else {
      localStorage.removeItem('coderated_auth_token');
      console.log('🔐 Token cleared');
    }
  }

  getStoredToken() {
    return localStorage.getItem('coderated_auth_token');
  }

  async request(endpoint, options = {}) {
    await this.initialize();

    if (!this.baseURL) {
      throw new Error('Backend API not available. Please check server status.');
    }

    // CRITICAL: Auto-restore token if available
    if (!this.token) {
      this.token = this.getStoredToken();
      if (this.token) {
        console.log('🔄 Token restored from localStorage:', this.token.substring(0, 20) + '...');
      } else {
        console.warn('⚠️ No token available for request to:', endpoint);
      }
    }

    // FIXED: Proper endpoint routing
    let finalEndpoint = endpoint;
    
    // Routes that should NOT have /api prefix
    const noApiPrefixRoutes = [
      '/health',
      '/api/auth/auth/login',    // FIXED: Use correct Swagger endpoints
      '/api/auth/auth/register', // FIXED: Use correct Swagger endpoints
      '/api/auth/auth/me',       // FIXED: Use correct Swagger endpoints
      '/api/analyze',            // FIXED: Use /api/analyze (from Swagger)
      '/api/websites/public',    // FIXED: Use /api/websites/public (from Swagger)
      '/api/users/usage',        // FIXED: Use /api/users/usage (from Swagger)
      '/api/users/me/usage',     // Enhanced: Support for user-specific usage endpoint
      '/analyses',
      '/analysis/',
      '/system/status',
      '/tasks/',
      '/campaigns',
      '/outreach/',
      '/monitoring/',
      '/analytics/',
      '/dev/'
    ];
    
    const shouldAddApiPrefix = !noApiPrefixRoutes.some(route => endpoint.startsWith(route)) && 
                              !endpoint.startsWith('/api/');
    
    if (shouldAddApiPrefix) {
      finalEndpoint = '/api' + endpoint;
    }

    const url = this.baseURL + finalEndpoint;
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    // CRITICAL: Always add Authorization header if token exists
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
      console.log('🔐 Auth header added for:', finalEndpoint);
    } else {
      console.warn('⚠️ No token available for:', finalEndpoint);
    }

    // Enhanced logging
    console.log(`🌐 Request to: ${url}`);
    console.log(`🔐 Has auth header: ${!!headers.Authorization}`);
    console.log(`📊 Method: ${options.method || 'GET'}`);

    // Implement retry logic
    for (let attempt = 0; attempt < this.retryCount; attempt++) {
      try {
        console.log(`🌐 Request attempt ${attempt + 1}/${this.retryCount} to:`, url);
        
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
          mode: 'cors',
          body: options.body ? JSON.stringify(options.body) : undefined,
        });
        
        console.log('📡 Response status:', response.status, 'for', url);

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Request successful:', url);
          return data;
        }

        // Handle specific error cases
        if (response.status === 401) {
          console.log('🔒 Authentication failed for endpoint:', finalEndpoint);
          
          const criticalEndpoints = ['/auth/me', '/api/users/me'];
          const isCriticalEndpoint = criticalEndpoints.some(critical => finalEndpoint.includes(critical));
          
          if (isCriticalEndpoint) {
            console.log('🗝️ Critical auth endpoint failed, clearing token');
            this.setAuthToken(null);
          }
          
          throw new Error('Authentication required. Please log in again.');
        }
        
        if (response.status === 403) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            errorData = { detail: 'Access forbidden' };
          }
          console.log('🚫 403 Forbidden response:', errorData);
          throw new Error(errorData.detail || 'Access forbidden. Check your permissions.');
        }
        
        if (response.status === 405) {
          console.log('❌ 405 Method Not Allowed for:', finalEndpoint);
          throw new Error(`Method not allowed for endpoint: ${finalEndpoint}`);
        }
        
        if (response.status === 404) {
          console.log('🔍 Endpoint not found:', finalEndpoint);
          throw new Error(`API endpoint not found: ${finalEndpoint}`);
        }

        if (response.status === 429) {
          // Rate limited - wait before retry
          if (attempt < this.retryCount - 1) {
            const waitTime = this.retryDelay * Math.pow(2, attempt);
            console.log(`⏳ Rate limited, waiting ${waitTime}ms...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue;
          }
        }

        if (response.status >= 500) {
          // Server error - retry if not last attempt
          if (attempt < this.retryCount - 1) {
            const waitTime = this.retryDelay * Math.pow(2, attempt);
            console.log(`🔄 Server error, retrying in ${waitTime}ms...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue;
          }
        }

        // Get error message
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.warn('Failed to parse error response:', parseError);
          errorData = { detail: 'HTTP ' + response.status + ' - ' + response.statusText };
        }
        
        const errorMessage = errorData?.detail || errorData?.message || ('HTTP ' + response.status);
        throw new Error(errorMessage);
        
      } catch (error) {
        console.error(`💥 Request attempt ${attempt + 1} failed:`, error);
        
        // Handle network errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          if (attempt < this.retryCount - 1) {
            const waitTime = this.retryDelay * Math.pow(2, attempt);
            console.log(`🔄 Network error, retrying in ${waitTime}ms...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue;
          }
          throw new Error('Network error: Cannot connect to backend. Please check if the server is running.');
        }
        
        if (error.name === 'AbortError') {
          if (attempt < this.retryCount - 1) {
            console.log(`⏰ Timeout, retrying...`);
            continue;
          }
          throw new Error('Request timeout: Backend server is not responding.');
        }
        
        // Re-throw other errors immediately
        throw error;
      }
    }
    
    throw new Error('Maximum retry attempts exceeded');
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, { 
      method: 'POST', 
      body: data 
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, { 
      method: 'PUT', 
      body: data 
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
  
  // FIXED: Login method with proper error handling
  async login(email, password) {
    console.log('🔐 Login attempt for:', email);
    
    try {
      await this.initialize();
      
      if (!this.baseURL) {
        throw new Error('Backend API not available. Please check server status.');
      }
      
      // Use the correct endpoint from Swagger UI: /api/auth/auth/login
      const response = await fetch(this.baseURL + '/api/auth/auth/login', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ email, password })
      });
      
      console.log('📡 Login response status:', response.status);
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.warn('Failed to parse login error response:', parseError);
          throw new Error(`Login failed: HTTP ${response.status}`);
        }
        console.log('❌ Login error response:', errorData);
        throw new Error(errorData.detail || errorData.message || 'Login failed');
      }
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse login success response:', parseError);
        throw new Error('Login response was not valid JSON');
      }
      
      console.log('✅ Login response:', { ...data, access_token: '[REDACTED]' });
      
      // Store the token
      if (data.access_token) {
        this.setAuthToken(data.access_token);
      }
      
      return data;
      
    } catch (error) {
      console.error('💥 Login error:', error);
      throw error;
    }
  }

  // Replace your register method in api.js with this:
  async register(name, email, password, tier = 'observer') {
    console.log('📝 Registration attempt for:', email, 'with tier:', tier);
    
    try {
      await this.initialize();
      
      if (!this.baseURL) {
        throw new Error('Backend API not available. Please check server status.');
      }
      
      // Use the correct endpoint from Swagger UI: /api/auth/auth/register  
      const response = await fetch(this.baseURL + '/api/auth/auth/register', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ name, email, password, tier })
      });
      
      console.log('📡 Registration response status:', response.status);
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.warn('Failed to parse registration error response:', parseError);
          throw new Error(`Registration failed: HTTP ${response.status}`);
        }
        console.log('❌ Registration error response:', errorData);
        throw new Error(errorData.detail || errorData.message || errorData.error || 'Registration failed');
      }
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse registration success response:', parseError);
        throw new Error('Registration response was not valid JSON');
      }
      
      console.log('✅ Registration response:', { ...data, access_token: data.access_token ? '[REDACTED]' : 'none' });
      
      // Registration always returns a token, store it
      if (data.access_token) {
        console.log('✅ Registration returned token, storing it');
        this.setAuthToken(data.access_token);
      } else {
        console.warn('⚠️ Registration succeeded but no token returned');
      }
      
      return data;
      
    } catch (error) {
      console.error('💥 Registration error:', error);
      throw error;
    }
  }

  async logout() {
    try {
        // First try the new endpoint structure
        await this.request('/api/auth/auth/logout', { 
            method: 'POST',
            credentials: 'include'
        });
    } catch (error) {
        console.warn('Logout request failed:', error.message);
        // Fallback to legacy endpoint
        try {
            await this.request('/auth/logout', { 
                method: 'POST',
                credentials: 'include'
            });
        } catch (secondError) {
            console.warn('Legacy logout also failed (non-critical):', secondError.message);
        }
    }
    
    // Always clear the token regardless of backend response
    this.setAuthToken(null);
    localStorage.removeItem('coderated_auth_token');
    
    return { success: true };
  }

  async getCurrentUser() {
    // Use the correct endpoint from Swagger UI: /api/auth/auth/me
    return this.request('/api/auth/auth/me', {
      method: 'GET'
    });
  }

  // FIXED: Website analysis with multiple endpoint fallbacks
  async analyzeWebsite(url, priority = 'normal') {
    console.log('🔬 Analyzing website:', url, 'with priority:', priority);
    
    const endpoints = [
      '/api/analyze',   // FIXED: Use the correct endpoint from Swagger
      '/api/search',
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Trying endpoint: ${endpoint}`);
        
        const body = endpoint === '/api/search' 
          ? { url }
          : { url, priority };
        
        const result = await this.request(endpoint, {
          method: 'POST',
          body
        });
        
        console.log(`✅ Success with endpoint: ${endpoint}`);
        return result;
        
      } catch (error) {
        console.log(`❌ Endpoint ${endpoint} failed:`, error.message);
        
        if (error.message.includes('Not authorized') || 
            error.message.includes('Insufficient permissions') ||
            error.message.includes('Forbidden') ||
            error.message.includes('forbidden')) {
          continue;
        }
        
        if (error.message.includes('Network') || error.message.includes('fetch')) {
          throw error;
        }
      }
    }
    
    throw new Error('Website analysis not available. Please check your account permissions or contact support.');
  }

  async getAnalyses(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get('/analyses' + (queryString ? '?' + queryString : ''));
  }

  async getAnalysisById(id) {
    return this.get('/analysis/' + id);
  }

  // ENHANCED: User usage tracking with multiple endpoint fallbacks
  async getUserUsage() {
    console.log('📊 Getting user usage...');
    
    // Try multiple possible endpoints in order of preference
    const endpoints = [
      '/api/users/me/usage',  // Most likely correct endpoint (user-specific)
      '/users/me/usage',      // Alternative without /api prefix
      '/api/users/usage',     // Original endpoint (may be for admin)
      '/users/usage'          // Fallback without /api prefix
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Trying usage endpoint: ${endpoint}`);
        const result = await this.get(endpoint);
        console.log(`✅ Success with endpoint: ${endpoint}`, result);
        return result;
      } catch (error) {
        console.log(`❌ Endpoint ${endpoint} failed:`, error.message);
        
        // If it's a 404, try next endpoint
        if (error.message.includes('404') || error.message.includes('not found')) {
          continue;
        }
        
        // For other errors (auth, etc), don't try other endpoints
        if (error.message.includes('401') || error.message.includes('403') || 
            error.message.includes('Authentication') || error.message.includes('Forbidden')) {
          throw error;
        }
      }
    }
    
    throw new Error('No working usage endpoint found. Please check your backend configuration.');
  }

  // ENHANCED: User usage update with multiple endpoint fallbacks
  async updateUserUsage(data) {
    console.log('📊 Updating user usage:', data);
    
    // Try multiple possible endpoints in order of preference
    const endpoints = [
      '/api/users/me/usage',  // Most likely correct endpoint (user-specific)
      '/users/me/usage',      // Alternative without /api prefix  
      '/api/users/usage',     // Original endpoint (may be for admin)
      '/users/usage'          // Fallback without /api prefix
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Trying update endpoint: ${endpoint}`);
        const result = await this.post(endpoint, data);
        console.log(`✅ Success with endpoint: ${endpoint}`, result);
        return result;
      } catch (error) {
        console.log(`❌ Update endpoint ${endpoint} failed:`, error.message);
        
        // If it's a 404, try next endpoint
        if (error.message.includes('404') || error.message.includes('not found')) {
          continue;
        }
        
        // For other errors (auth, etc), don't try other endpoints
        if (error.message.includes('401') || error.message.includes('403') || 
            error.message.includes('Authentication') || error.message.includes('Forbidden')) {
          throw error;
        }
      }
    }
    
    throw new Error('No working usage update endpoint found. Please check your backend configuration.');
  }

  // FIXED: Public websites endpoint
  async getPublicWebsites() {
    return this.get('/api/websites/public');  // FIXED: Use correct endpoint
  }

  // Review endpoints
  async createReview(reviewData) {
    return this.post('/reviews', reviewData);
  }

  async getReviews() {
    return this.get('/reviews');
  }

  async getReviewById(id) {
    return this.get('/reviews/' + id);
  }

  async updateReview(id, reviewData) {
    return this.put('/reviews/' + id, reviewData);
  }

  async deleteReview(id) {
    return this.delete('/reviews/' + id);
  }

  // Task status endpoints
  async getTaskStatus(taskId) {
    return this.get('/tasks/' + taskId + '/status');
  }

  async getQueueStatus() {
    return this.get('/tasks/queue/status');
  }

  // System status
  async getSystemStatus() {
    return this.get('/system/status');
  }

  async checkHealth() {
    return this.get('/health');
  }

  // Admin endpoints
  async getAllUsers() {
    return this.get('/admin/users');
  }

  async promoteUser(userId) {
    return this.request('/admin/user/' + userId + '/promote', { method: 'PATCH' });
  }

  async demoteUser(userId) {
    return this.request('/admin/user/' + userId + '/demote', { method: 'PATCH' });
  }

  async getAdminStats() {
    return this.get('/admin/stats');
  }

  // Debug utilities
  getDebugInfo() {
    return {
      environment: this.environment,
      baseURL: this.baseURL,
      hasToken: !!this.token,
      isInitialized: this.isInitialized,
      storedToken: !!this.getStoredToken(),
      retryCount: this.retryCount,
      currentToken: this.token ? this.token.substring(0, 20) + '...' : null
    };
  }

  async testConnection() {
    try {
      console.log('🔍 Testing connection to:', this.baseURL);
      const response = await fetch(this.baseURL + '/health', {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      console.log('📡 Health check response:', response.status, response.statusText);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Health check data:', data);
        return { success: true, message: 'Backend connection successful', data };
      } else {
        return { success: false, message: `Backend returned ${response.status}: ${response.statusText}` };
      }
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      return { success: false, message: error.message };
    }
  }
}

export const apiClient = new APIClient();

// Make apiClient globally available for debugging
if (typeof window !== 'undefined') {
  window.apiClient = apiClient;
}

export default apiClient;

// Legacy functions for backward compatibility
export const fetchAllUsers = async (token) => {
  try {
    apiClient.setAuthToken(token);
    return await apiClient.getAllUsers();
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const promoteUser = async (userId, token) => {
  try {
    apiClient.setAuthToken(token);
    return await apiClient.promoteUser(userId);
  } catch (error) {
    console.error('Error promoting user:', error);
    throw error;
  }
};

export const demoteUser = async (userId, token) => {
  try {
    apiClient.setAuthToken(token);
    return await apiClient.demoteUser(userId);
  } catch (error) {
    console.error('Error demoting user:', error);
    throw error;
  }
};

export const fetchAdminStats = async (token) => {
  try {
    apiClient.setAuthToken(token);
    return await apiClient.getAdminStats();
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    throw error;
  }
};